import React, { useMemo } from 'react'
import { useSelector } from 'react-redux'
import { useIntl } from 'react-intl'
import { Paper, Box } from '@mui/material'

import { TARIFFS_TABLE_ACTIONS, TARIFFS_TABLE_COLUMNS } from '../../constants'
import { TableActions } from '../table-actions'
import { TableActive } from '../table-active'
import { TableUrlPagination } from '../../../../ui'
import { tariffsTableStyles } from './tariffs-table.styles'

const getLookup = (combo) => {
  let lookup = {}
  combo.forEach((item) => (lookup[item.key] = item.value))
  return lookup
}

export const TariffsTable = () => {
  const { formatMessage } = useIntl()
  const {
    data,
    search,
    pagination: { total_tarifas: totalEntries },
    isLoading,
  } = useSelector((state) => state.tariffs)
  const combos = useSelector((state) => state.combos)

  //Column structure
  const formattedColumns = useMemo(
    () =>
      [...TARIFFS_TABLE_COLUMNS, ...TARIFFS_TABLE_ACTIONS].map((column) => {
        // Base column configuration for Material React Table
        const baseColumn = {
          accessorKey: column.field,
          id: column.field,
          size: column.width,
          minSize: column.minWidth || column.width,
          maxSize: column.maxWidth,
          enableSorting: column.sorting !== false,
          muiTableHeadCellProps: {
            align: column.align || 'left',
          },
          muiTableBodyCellProps: {
            align: column.align || 'left',
            sx: {
              minWidth: column.minWidth || column.width,
            },
          },
        }

        // Handle special action columns
        if (column.field === 'actions') {
          return {
            ...baseColumn,
            header: formatMessage({ id: `global.${column.field}` }),
            Cell: ({ row }) => <TableActions row={row} />,
          }
        }

        if (column.field === 'active') {
          return {
            ...baseColumn,
            header: formatMessage({ id: `global.${column.field}` }),
            Cell: ({ row }) => <TableActive row={row} />,
          }
        }

        // Handle regular data columns
        const result = {
          ...baseColumn,
          header: formatMessage({ id: `pages.tariffs.table.columns.${column.field}.title` }),
        }

        // Add lookup functionality if combo data exists
        if (
          column.hasOwnProperty('comboId') &&
          combos[column.comboId] &&
          combos[column.comboId].data
        ) {
          const lookup = getLookup(combos[column.comboId].data)
          result.Cell = ({ cell }) => {
            const value = cell.getValue()
            return lookup[value] || value || <span>--</span>
          }
        } else {
          // Default cell renderer with empty value handling
          result.Cell = ({ cell }) => {
            const value = cell.getValue()
            return value || <span></span>
          }
        }

        return result
      }),
    [combos, formatMessage] // eslint-disable-line react-hooks/exhaustive-deps
  )

  //Assembling and adjusting data
  const processedData = useMemo(
    () =>
      data.map((item) => {
        const hasProvincias = Array.isArray(item.provincias) && item.provincias.length > 0
        return {
          ...item,
          nombre: item.nombre.toUpperCase(),
          provincias: hasProvincias
            ? item.provincias
                .map((provincia) =>
                  typeof provincia === 'string' && provincia
                    ? provincia.charAt(0).toUpperCase() + provincia.slice(1).toLowerCase()
                    : provincia || ''
                )
                .join(', ')
            : '',
          comunidad: hasProvincias && item.comunidad ? item.comunidad : '--',
          tableData: {
            ...item.tableData,
          },
        }
      }),
    [data]
  )

  //Table configuration
  const tableOptions = useMemo(
    () => ({
      toolbar: false,
      selection: false,
      fixedColumns: {
        right: 2,
      },
      headerStyle: tariffsTableStyles.header,
      draggable: false,
      muiTableProps: {
        sx: {
          tableLayout: 'fixed',
        },
      },
    }),
    []
  )

  return (
    <TableUrlPagination
      columns={formattedColumns}
      options={tableOptions}
      totalEntries={totalEntries}
      search={search}
      data={processedData}
      isLoading={isLoading}
    />
  )
}
